<!--
  //app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsComparison.vue
  -->

<script setup lang="ts">
import type { Team } from "~/src/team/domain/models/Team";
import type { Player } from "~/src/player/domain/Player";
import type { MatchStatsTeam } from "~/src/stats/domain/models/MatchStats";
import type { Parameter } from "~/src/stats/domain/models/Parameter";
import type { Event } from "~/src/match/domain/models/Event";
import type { MatchStatusSimplified } from "~/src/match/domain/models/Match";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import matchService from "~/src/match/domain/services/matchService";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import CardPlayerMvp from "~/app/components/pages/region-stats/CardPlayerMvp.vue";
import MatchStatsLogosHeader from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsLogosHeader.vue";
import MatchStatsTeamStats from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsTeamStats.vue";
import MatchStatsPlayerStats from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsPlayerStats.vue";

type Props = {
  seasonId: number;
  seasonClusterId: number;
  parameters: Parameter[];
  matchStatus: MatchStatusSimplified | undefined;
  matchEvents: Event[];
  players: Player[];
  mvpPartner?: SeasonStatsPartner;
  homeTeamInfo: Team;
  awayTeamInfo: Team;
  homeTeamTotalScore: number | null | undefined;
  awayTeamTotalScore: number | null | undefined;
  homeTeamStatsData: MatchStatsTeam;
  awayTeamStatsData: MatchStatsTeam;
};

const props = defineProps<Props>();
const { currentSeason } = useCompetitionStore();
const { isOwnGoal } = matchService();
const { t } = useI18n();

const data = reactive({
  isContainerVisible: true,
  svgTransformClass: "rotate-180",
});

const mvpPartner = currentSeason ? currentSeason?.metaInformation?.stats_partner : undefined;

const filterTeamEvents = (events: Event[] | undefined | null, teamId: number): Event[] => {
  if (!events || !events.length || !teamId) return [];

  return events.filter(me => {
    const _isOwnGoal = isOwnGoal(me.parameterAppString);
    return (!_isOwnGoal && me.teamId === teamId) || (me.teamId !== teamId && _isOwnGoal);
  });
};
const awayTeamMatchEvents = filterTeamEvents(props.matchEvents, props.awayTeamInfo.id);
const homeTeamMatchEvents = filterTeamEvents(props.matchEvents, props.homeTeamInfo.id);

const toggleContainerVisibility = () => {
  data.isContainerVisible = !data.isContainerVisible;
  data.svgTransformClass = data.isContainerVisible ? "rotate-180" : "rotate-0";
};

const findPlayerById = (playerId: number): Player | undefined => {
  return props.players.find(player => player.id === playerId);
};

const mvpPlayer = computed((): { player: Player | undefined; teamLogoUrl: string | undefined } => {
  if (!homeTeamMatchEvents || !awayTeamMatchEvents) {
    return { player: undefined, teamLogoUrl: undefined };
  }

  // Check if MVP is from home team
  const mvpEventHome = homeTeamMatchEvents.find(event => event.parameterAppString.includes("MVP"));
  if (mvpEventHome) {
    const player = mvpEventHome.playerId ? findPlayerById(mvpEventHome.playerId) : undefined;
    return { player, teamLogoUrl: props.homeTeamInfo.logo.url };
  }

  // Check if MVP is from away team
  const mvpEventAway = awayTeamMatchEvents.find(event => event.parameterAppString.includes("MVP"));
  if (mvpEventAway) {
    const player = mvpEventAway.playerId ? findPlayerById(mvpEventAway.playerId) : undefined;
    return { player, teamLogoUrl: props.awayTeamInfo.logo.url };
  }
  // If no MVP is found
  return { player: undefined, teamLogoUrl: undefined };
});
</script>

<template>
  <div class="w-full bg-gray-900 lg:rounded-lg">
    <!--    LOGOS -->
    <match-stats-logos-header
      :home-team-first-color="homeTeamInfo.firstColorHEX"
      :away-team-first-color="awayTeamInfo.firstColorHEX"
      :home-team-second-color="homeTeamInfo.secondColorHEX"
      :away-team-second-color="awayTeamInfo.secondColorHEX"
      :home-team-logo-url="homeTeamInfo.logo.url"
      :away-team-logo-url="awayTeamInfo.logo.url"
      :away-team-complete-name="awayTeamInfo.completeName"
      :home-team-complete-name="homeTeamInfo.completeName"
      :home-team-id="homeTeamInfo.id"
      :away-team-id="awayTeamInfo.id"
    />

    <div class="lg:px-24 lg:py-6 px-4 py-9">
      <!--      Team STATS-->

      <match-stats-team-stats
        :match-status="matchStatus"
        :players="players"
        :parameters="parameters"
        :home-team-info="homeTeamInfo"
        :away-team-info="awayTeamInfo"
        :match-events="matchEvents"
        :home-team-stats-data="homeTeamStatsData"
        :away-team-stats-data="awayTeamStatsData"
        :home-team-total-score="homeTeamTotalScore"
        :away-team-total-score="awayTeamTotalScore"
        :season-cluster-id="seasonClusterId"
      />

      <button
        @click="toggleContainerVisibility"
        class="text-gray-400 bg-gray-800 text-xs lg:hidden rounded-lg p-4 w-full"
      >
        {{ $t("match.stats.playerStats.title") }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          class="inline-block"
          :class="data.svgTransformClass"
        >
          <path
            d="M6.50875 9.07598C6.31781 9.07594 6.1347 9.00231 5.99971 8.8713L3.11971 6.07702C3.05094 6.01258 2.99609 5.93549 2.95836 5.85027C2.92062 5.76504 2.90076 5.67337 2.89993 5.58062C2.8991 5.48786 2.91732 5.39587 2.95352 5.31002C2.98972 5.22417 3.04318 5.14617 3.11079 5.08058C3.17839 5.01499 3.25878 4.96312 3.34726 4.928C3.43575 4.89287 3.53056 4.8752 3.62616 4.876C3.72176 4.87681 3.81624 4.89608 3.90408 4.93269C3.99192 4.9693 4.07137 5.02252 4.13779 5.08924L6.50875 7.38963L8.87971 5.08924C9.0155 4.96199 9.19738 4.89158 9.38616 4.89317C9.57494 4.89476 9.75553 4.96823 9.88902 5.09775C10.0225 5.22727 10.0982 5.40248 10.0999 5.58564C10.1015 5.76881 10.0289 5.94527 9.89779 6.07702L7.01779 8.8713C6.88279 9.00231 6.69969 9.07594 6.50875 9.07598Z"
            fill="#686868"
          />
        </svg>
      </button>

      <!--      Player STATS-->

      <div class="kql-player-stats-container" v-if="data.isContainerVisible">
        <div class="mb-6 mt-10">
          <match-stats-player-stats
            :home-team-info="homeTeamInfo"
            :away-team-stats-data="awayTeamStatsData"
            :away-team-info="awayTeamInfo"
            :home-team-stats-data="homeTeamStatsData"
            :parameters="parameters"
            :seasonId="seasonId"
          />

          <div v-if="mvpPlayer && mvpPlayer.teamLogoUrl && mvpPlayer.player?.image?.url">
            <p class="text-primary text-center text-xs mb-4 mt-8">{{ $t("match.stats.turn.mvp") }}</p>
            <div class="mx-auto w-80">
              <card-player-mvp
                v-if="mvpPlayer && mvpPlayer.teamLogoUrl && mvpPlayer.player?.image?.url"
                :featured-player-image="mvpPlayer.player.image.url"
                :featured-player-name="mvpPlayer.player.shortName"
                :featured-player-team-image="mvpPlayer.teamLogoUrl"
                :featured-player-stat="null"
                turn-number="0"
                :has-partner="!!mvpPartner"
                :partner-logo="mvpPartner?.logo"
                :partner-name="mvpPartner?.name"
                :table-stat-title="$t('general.stats.mvps')"
                page-stats-link="Mvp"
                hide-turn
                hide-stat
              ></card-player-mvp>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
